import { faCartShopping } from "@fortawesome/pro-regular-svg-icons"
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material"
import { FaIcon } from "@skistarab/mui-theme"
import { useTranslation } from "react-i18next"

import useProductTotals from "@/hooks/useProductTotals"

import StyledHelperText from "../StyledHelperText"

type PriceSummeryBarProps = {
  buttonText: string
  isLoading?: boolean
  error?: string | null
  showCartIcon?: boolean
  onClick?: () => void
  isSummer?: boolean
}

const PriceSummeryBar = ({
  buttonText,
  isLoading,
  error,
  showCartIcon,
  onClick,
  isSummer = false,
}: PriceSummeryBarProps) => {
  const { t } = useTranslation()

  const { totalQuantity, totalPriceText } = useProductTotals()

  return (
    <AppBar
      position="fixed"
      component="div"
      role="region"
      aria-label={t("priceSummaryBar.ariaLabel")}
      sx={{
        top: "auto",
        bottom: 0,
        zIndex: 800,
        padding: 2,
        backgroundColor: "background.default",
        boxShadow: "0px -2px 8px 0px rgba(0, 0, 0, 0.15)",
      }}
    >
      <Container sx={{ px: 0 }}>
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={{ md: 7, xs: 2 }}
          sx={{
            width: "100%",
            justifyContent: { xs: "space-between", md: "flex-end" },
          }}
        >
          <Box
            sx={{
              minWidth: { xs: 0, md: 160 },
              flex: { xs: "1 1 auto", md: "0 0 auto" },
              textAlign: "left",
            }}
          >
            <Typography
              variant="h8"
              component="span"
              fontWeight="bold"
              sx={{ typography: { md: "h6" } }}
            >
              {totalPriceText}
            </Typography>
            <Typography
              variant="body3"
              color="text.secondary"
              sx={{ typography: { md: "body2" } }}
            >
              {t("totalPriceBar.quantityLabel", {
                count: totalQuantity,
                context: isSummer ? "summer" : undefined,
              })}
            </Typography>
          </Box>
          <Button
            variant="contained"
            disabled={isLoading}
            onClick={onClick}
            startIcon={
              isLoading ? (
                <CircularProgress size={20} />
              ) : showCartIcon ? (
                <FaIcon icon={faCartShopping} />
              ) : undefined
            }
            sx={{
              borderRadius: 8,
              px: { xs: 2, md: 4 },
              py: 1.5,
            }}
          >
            {isLoading ? t("totalPriceBar.addingToCart") : buttonText}
          </Button>
        </Stack>
        {error && (
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            mt={1}
          >
            <StyledHelperText helperText={error} severity="error" />
          </Stack>
        )}
      </Container>
    </AppBar>
  )
}

export default PriceSummeryBar
